import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Activity, Database, AlertCircle } from 'lucide-react';

export default function AdminToolsPage() {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState<'matomo' | 'schema'>('matomo');

    return (
        <div className="space-y-6 animate-in fade-in duration-700">
            {/* Header */}
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-3xl font-black text-white uppercase tracking-tight mb-2">
                        {t('admin.tools.title')}
                    </h1>
                    <p className="text-gray-400 max-w-2xl font-light">
                        {t('admin.tools.subtitle')}
                    </p>
                </div>

                <div className="flex bg-gray-900/50 p-1.5 rounded-2xl border border-white/5">
                    <button
                        onClick={() => setActiveTab('matomo')}
                        className={`px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all flex items-center gap-3 ${activeTab === 'matomo'
                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20'
                            : 'text-gray-500 hover:text-white hover:bg-white/5'
                            }`}
                    >
                        <Activity className="w-4 h-4" />
                        {t('admin.tools.tabMatomo')}
                    </button>
                    <button
                        onClick={() => setActiveTab('schema')}
                        className={`px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all flex items-center gap-3 ${activeTab === 'schema'
                            ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/20'
                            : 'text-gray-500 hover:text-white hover:bg-white/5'
                            }`}
                    >
                        <Database className="w-4 h-4" />
                        {t('admin.tools.tabSchema')}
                    </button>
                </div>
            </div>

            {/* Matomo View */}
            {activeTab === 'matomo' && (
                <div className="bg-gray-900 border border-white/5 rounded-[2.5rem] overflow-hidden h-[800px] relative shadow-2xl">
                    <iframe
                        src="/matomo/"
                        className="w-full h-full border-0"
                        title="Matomo Analytics"
                    />
                    <div className="absolute top-0 right-0 m-4">
                        <a
                            href="/matomo/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-black/50 hover:bg-blue-600 text-white p-2 rounded-lg text-[10px] font-bold uppercase tracking-widest backdrop-blur-md transition-colors border border-white/10"
                        >
                            {t('admin.tools.openNewTab')} ↗
                        </a>
                    </div>
                </div>
            )}

            {/* SchemaSpy View */}
            {activeTab === 'schema' && (
                <div className="bg-white rounded-[2.5rem] overflow-hidden h-[800px] border border-white/5 relative shadow-2xl">
                    <iframe
                        src="/schemaspy/index.html"
                        className="w-full h-full border-0"
                        title="Database Schema"
                        onError={(e) => console.error("Error loading schema", e)}
                    />

                    {/* Fallback Overlay (z-index negative so it stays behind iframe, visible if iframe is transparent/fails) */}
                    <div className="absolute -z-10 inset-0 flex flex-col items-center justify-center bg-gray-900 text-gray-500 p-10 text-center">
                        <div className="p-4 bg-gray-800 rounded-full mb-4 border border-white/5">
                            <AlertCircle className="w-8 h-8 text-amber-500" />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2 uppercase tracking-tight">{t('admin.tools.docNotGenerated')}</h3>
                        <p className="max-w-md mb-6 font-light">
                            {t('admin.tools.docNotGeneratedDesc')}
                        </p>
                        <code className="bg-black/50 px-6 py-3 rounded-xl text-xs text-green-400 font-mono border border-white/5">
                            ./generate_schemaspy.sh
                        </code>
                    </div>
                </div>
            )}
        </div>
    );
}
